import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Search, Menu, X, MapPin, User } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const Header = () => {
    const { cartCount, wishlist } = useShop();
    const { customer } = useCustomerAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [search, setSearch] = useState("");
    const nav = useNavigate();

    const submitSearch = (e) => {
        e.preventDefault();
        if (search.trim()) nav(`/shop?search=${encodeURIComponent(search.trim())}`);
    };

    return (
        <header
            className="sticky top-0 z-40 border-b border-[color:var(--border-gold)]"
            style={{ backgroundColor: "rgba(251,245,233,0.98)" }}
            data-testid="site-header"
        >
            <div className="container-x">
                {/* Top row: logo + search + right icons */}
                <div className="grid grid-cols-[1fr_auto_1fr] md:grid-cols-3 items-center py-4">
                    {/* Left nav (desktop) / Mobile menu button */}
                    <div className="flex items-center gap-6">
                        <button
                            className="md:hidden text-[color:var(--maroon)]"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                        <nav className="hidden md:flex items-center gap-7 font-ui text-xs tracking-widest-x uppercase text-[color:var(--maroon)]">
                            <Link to="/shop" className="hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-shop">Shop</Link>
                            <Link to="/shop?featured=true" className="hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-collections">Collections</Link>
                            <Link to="/shop?new=true" className="hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-new">New Arrivals</Link>
                        </nav>
                    </div>

                    {/* Logo center */}
                    <Link to="/" className="flex flex-col items-center justify-center group" data-testid="logo-link">
                        <span className="font-heading text-2xl md:text-3xl tracking-[0.15em] text-[color:var(--maroon)] leading-none">
                            KANGANYA
                        </span>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className="w-6 h-px bg-[color:var(--gold)]" />
                            <span className="w-1.5 h-1.5 rotate-45 bg-[color:var(--gold)]" />
                            <span className="w-6 h-px bg-[color:var(--gold)]" />
                        </div>
                        <span className="font-devanagari text-lg md:text-xl text-[color:var(--maroon)] mt-1 leading-none">
                            कंगन्या
                        </span>
                        <span className="font-ui text-[8px] sm:text-[9px] md:text-[10px] tracking-[0.12em] sm:tracking-[0.2em] uppercase whitespace-nowrap text-[color:var(--gold)] mt-1.5">
                            Bangles · Bridal · Heritage
                        </span>
                    </Link>

                    {/* Right nav */}
                    <div className="flex items-center justify-end gap-4 md:gap-6 text-[color:var(--maroon)]">
                        <Link to="/track" className="hidden lg:flex items-center gap-1.5 text-xs font-ui tracking-widest-x uppercase hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-track">
                            <MapPin size={15} strokeWidth={1.5} /> Track
                        </Link>
                        <button
                            onClick={() => navigate(customer ? "/account" : "/login")}
                            className="hidden md:flex items-center gap-1.5 text-xs font-ui tracking-widest-x uppercase hover:text-[color:var(--copper)] transition-colors duration-300"
                            data-testid="nav-login"
                        >
                            <User size={15} strokeWidth={1.5} /> {customer ? customer.name?.split(" ")[0] : "Login"}
                        </button>
                        <Link to="/wishlist" className="relative hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-wishlist">
                            <Heart size={20} strokeWidth={1.4} />
                            {wishlist.length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 text-[10px] bg-[color:var(--maroon)] text-[color:var(--ivory)] rounded-full w-4 h-4 flex items-center justify-center font-ui" data-testid="wishlist-count">
                                    {wishlist.length}
                                </span>
                            )}
                        </Link>
                        <Link to="/cart" className="relative hover:text-[color:var(--copper)] transition-colors duration-300" data-testid="nav-cart">
                            <ShoppingBag size={20} strokeWidth={1.4} />
                            {cartCount > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 text-[10px] bg-[color:var(--maroon)] text-[color:var(--ivory)] rounded-full w-4 h-4 flex items-center justify-center font-ui" data-testid="cart-count">
                                    {cartCount}
                                </span>
                            )}
                        </Link>
                    </div>
                </div>

                {/* Search bar */}
                <form onSubmit={submitSearch} className="pb-3 hidden md:flex items-center max-w-md mx-auto gap-2" data-testid="header-search-form">
                    <Search size={15} className="text-[color:var(--ink-muted)]" strokeWidth={1.5} />
                    <input
                        className="field !py-1.5 text-sm !border-none focus:!border-none"
                        placeholder="Search bangles by material, region, occasion…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="header-search-input"
                    />
                </form>
            </div>


            {/* Mobile menu */}
            {mobileOpen && (
                <div className="md:hidden border-t border-[color:var(--border-gold)] bg-[color:var(--ivory)]" data-testid="mobile-menu">
                    <div className="container-x py-4 space-y-3 font-ui text-sm tracking-widest-x uppercase text-[color:var(--maroon)]">
                        <form
                            onSubmit={(e) => { submitSearch(e); setMobileOpen(false); }}
                            className="flex items-center gap-2 border border-[color:var(--border-gold)] px-3 py-2 mb-1"
                            data-testid="mobile-search-form"
                        >
                            <Search size={15} className="text-[color:var(--ink-muted)] shrink-0" strokeWidth={1.5} />
                            <input
                                className="flex-1 bg-transparent border-none focus:outline-none normal-case tracking-normal font-body text-sm text-[color:var(--ink)] placeholder:italic"
                                placeholder="Search bangles…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="mobile-search-input"
                            />
                        </form>
                        <Link to="/shop" onClick={() => setMobileOpen(false)} className="block py-2">Shop All</Link>
                        <Link to="/track" onClick={() => setMobileOpen(false)} className="block py-2 border-t border-[color:var(--border-gold)]">Track Order</Link>
                        <Link to="/story" onClick={() => setMobileOpen(false)} className="block py-2">Our Story</Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;